<template>
    <v-card v-bind="$attrs" class="linked-account-card" outlined>
        <header class="linked-account-card__header">
            <span class="linked-account-card__icon" :class="statusClass" aria-hidden="true">
                <v-icon>{{ value && value.completed ? mdiProgressCheck : mdiProgressAlert }}</v-icon>
            </span>
            <div class="linked-account-card__copy">
                <span class="linked-account-card__eyebrow">ACCOUNT ACCESS</span>
                <h3>Linked account</h3>
                <p>{{ statusLabel }}</p>
            </div>
            <v-btn
                v-if="edit && !(value && value.verifiedAt)"
                class="linked-account-card__edit"
                icon
                aria-label="Edit linked account"
                @click="$emit('edit')"
            >
                <v-icon>{{ mdiPencil }}</v-icon>
            </v-btn>
        </header>

        <div v-if="value && value.email" class="linked-account-card__details">
            <div class="linked-account-card__email">
                <small>Linked email</small>
                <strong>{{ value.email }}</strong>
            </div>
            <div class="linked-account-card__dates">
                <span v-if="startedAt"><small>Started</small>{{ startedAt }}</span>
                <span v-if="completedAt"><small>Verified</small>{{ completedAt }}</span>
            </div>
        </div>
        <div v-else class="linked-account-card__empty">
            Link a previous Getac account to keep its service history connected to this profile.
        </div>
    </v-card>
</template>

<script>
import Vue from 'vue'
import { mdiProgressCheck, mdiProgressAlert, mdiPencil } from '@mdi/js'

export default Vue.extend({
    name: 'AccountLinkedAccount',
    inheritAttrs: false,
    props: {
        value: {
            type: Object,
            required: true,
            default() {
                return {}
            }
        },
        edit: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            mdiProgressAlert,
            mdiProgressCheck,
            mdiPencil
        }
    },
    computed: {
        statusLabel() {
            if (this.value && this.value.verifiedAt) return 'Linked and verified'
            if (this.value && this.value.email) return 'Verification in progress'
            return 'Connect another account'
        },
        statusClass() {
            return this.value && this.value.verifiedAt ? 'linked-account-card__icon--verified' : ''
        },
        startedAt() {
            return this.formatDate(this.value && this.value.createdAt)
        },
        completedAt() {
            return this.formatDate(this.value && this.value.verifiedAt)
        }
    },
    methods: {
        formatDate(value) {
            if (!value) return ''
            const date = new Date(value)
            return Number.isNaN(date.getTime()) ? '' : date.toLocaleString()
        }
    }
})
</script>

<style scoped>
.linked-account-card {
    padding: 17px;
    border-radius: 9px;
}
.linked-account-card.theme--dark {
    color: #f1f2f5;
    background: rgba(23, 25, 31, 0.38);
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    box-shadow: none !important;
}
.linked-account-card__header {
    display: flex;
    align-items: center;
    gap: 11px;
}
.linked-account-card__icon {
    display: grid;
    width: 36px;
    height: 36px;
    flex: 0 0 36px;
    color: #ffb393;
    background: rgba(219, 81, 32, 0.12);
    border: 1px solid rgba(240, 120, 76, 0.25);
    border-radius: 10px;
    place-items: center;
}
.linked-account-card__icon .v-icon {
    color: inherit;
    font-size: 19px;
}
.linked-account-card__icon--verified {
    color: #9be0b3;
    background: rgba(63, 177, 108, 0.1);
    border-color: rgba(88, 201, 135, 0.25);
}
.linked-account-card__copy {
    min-width: 0;
    flex: 1;
}
.linked-account-card__eyebrow {
    color: #f2a58b;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 1.2px;
}
.linked-account-card__copy h3 {
    margin: 2px 0 1px;
    color: inherit;
    font-size: 15px;
    font-weight: 600;
}
.linked-account-card__copy p {
    margin: 0;
    color: #aeb5c3;
    font-size: 10px;
}
.linked-account-card__edit.v-btn {
    color: #ff9a74;
    background: rgba(219, 81, 32, 0.09);
    border: 1px solid rgba(240, 120, 76, 0.23);
}
.linked-account-card__details,
.linked-account-card__empty {
    margin: 14px 0 0 47px;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.09);
}
.linked-account-card__email {
    min-width: 0;
    color: #f1f2f5;
    font-size: 12px;
    overflow-wrap: anywhere;
}
.linked-account-card__email small,
.linked-account-card__dates small {
    display: block;
    margin-bottom: 3px;
    color: #929bad;
    font-size: 9px;
}
.linked-account-card__email strong {
    font-weight: 500;
}
.linked-account-card__dates {
    display: flex;
    flex-wrap: wrap;
    gap: 18px;
    margin-top: 10px;
    color: #bfc4ce;
    font-size: 10px;
}
.linked-account-card__empty {
    color: #aeb5c3;
    font-size: 11px;
    line-height: 1.6;
}
.theme--light.linked-account-card .linked-account-card__eyebrow {
    color: #ba431b;
}
.theme--light.linked-account-card .linked-account-card__copy p,
.theme--light.linked-account-card .linked-account-card__empty {
    color: #6a6e79;
}
.theme--light.linked-account-card .linked-account-card__details {
    border-top-color: rgba(56, 62, 74, 0.12);
}
.theme--light.linked-account-card .linked-account-card__email {
    color: #30333b;
}
.theme--light.linked-account-card .linked-account-card__dates {
    color: #6a6e79;
}
@media (max-width: 480px) {
    .linked-account-card__details,
    .linked-account-card__empty {
        margin-left: 0;
    }
}
</style>
